import reactUpdate, {Spec} from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import {Entry, EntryProtocol, FromServerEntry, ToServerEntry} from 'notebook/ELNConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import * as NoteStoreTransforms from 'notebook/notes/NoteStoreTransforms';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {NoteDay} from 'notebook/util/NoteUtilConstants';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import pick from 'util/pick';

import EntryVersionUtil from './EntryVersionUtil';

const EntryUtil = {
  /**
   * Helper for abstracting away the path to the entryVersion on an entry object.
   */
  entryVersionUpdate(op: Spec<Entry>): Spec<Entry> {
    // Because an entry's object does not nest an entry version, the entry is effectively an entry version,
    // so this just returns the op. It would have been better if we nested the entryVersion
    // (e.g. see DocumentUtil.entryVersionUpdate), but such a refactor would be large and risky.
    return op;
  },

  pickRevertableFields(entry: Entry): {
    name: string;
    note: EntryNote;
    entryProtocols: Array<EntryProtocol>;
    structuredTableMappings: Array<StructuredTableMapping>;
  } {
    // eslint-disable-next-line no-restricted-syntax
    return _.extend(pick(entry, 'name'), EntryVersionUtil.pickRevertableFields(entry));
  },

  sanitizeEntry(entry: Entry, oldEntry: Entry | null = null): Entry {
    return EntryVersionUtil.sanitizeEntryVersion(entry, oldEntry, entry.id);
  },

  prepareForStore(entry: FromServerEntry, cidIdMap: CidIdMap): Entry {
    const entryFields = [
      'id',
      'versionId',
      'folder',
      'editURL',
      'name',
      'permissions',
      'review',
      'tags',
      'tagSchema',
      'created_at',
      'modified_at',
      'thirdPartyStorageServiceData',
      'humanId',
      'templateVersionId',
      'templateUrl',
      'templateVersionTimestamp',
      'authors',
      'archiveRecord',
      'lock',
    ] as const;

    // TODO(T1044): EntryVersion is merged with Entry in the server payload, so we omit entry-specific fields
    // in preparing the EntryVersion (and add in the entry version id), and then recombine with the
    // entry-specific fields for the store.
    const entryVersion = EntryVersionUtil.prepareForStore(
      (
        _.chain(entry)
          .omit(...entryFields)
          .extend({id: entry.versionId}) as {
          // Underscore's chained object value is incorrectly declared as an array in its type definitions.
          value(): Parameters<typeof EntryVersionUtil.prepareForStore>[0];
        } & _._Chain<FromServerEntry>
      ).value(),
      cidIdMap,
      entry.id
    );
    return {...entryVersion, ...pick(entry, ...entryFields), isStale: false} as Entry;
  },

  prepareForSave(entry: Entry, cidIdMap: CidIdMap): ToServerEntry {
    const entryVersion = EntryVersionUtil.prepareForSave(entry, cidIdMap, entry.id);
    return {...entryVersion, ...pick(entry, 'name')};
  },

  /**
   * Prepares an entry for recovery from localStorage.
   *
   * This method is similar to prepareForSave but specifically handles the recovery case
   * where we need to prevent the old entry ID from being embedded in structured tables.
   * Passing null as the fileId allows the server to assign the correct new entry ID
   * during recovery.
   *
   * @param entry - The entry to prepare for recovery
   * @param cidIdMap - Map of client IDs to server IDs
   */
  prepareForRecovery(entry: Entry, cidIdMap: CidIdMap): ToServerEntry {
    const entryVersion = EntryVersionUtil.prepareForSave(entry, cidIdMap, null);
    return {...entryVersion, ...pick(entry, 'name')};
  },

  addTasksToEntry(entry: Entry): Entry {
    return reactUpdate(entry, {note: {$set: NoteStoreTransforms.addTasksToNote(entry.note)}});
  },

  removeTasksFromEntry(entry: Entry): Entry {
    if (entry.note == null) {
      return entry;
    }
    return reactUpdate(entry, {note: {$set: NoteStoreTransforms.removeTasksFromNote(entry.note)}});
  },

  // Returns the day of the current note part.
  getDayOfNotePart(index: number, days: Array<NoteDay>): NoteDay {
    const daysUpToCurrent = days.filter((d) => d.index <= index);
    assert(daysUpToCurrent.length > 0, 'At least one day is expected before the current index.');
    return _.max(daysUpToCurrent, (d) => d.index);
  },
};

export default EntryUtil;
